import type { FC } from 'react'

import { useNotification } from 'app/providers'
import { Button } from 'common/components/Button/Button'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  BUTTON_CONSTANTS,
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
} from 'common/constants/buttonConstants'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { JitsiTelehealthCallButton } from 'features/JitsiTelehealth'

import styles from './ehrAppointmentV2.module.scss'

type Props = {
  appointment: IPatientSchedule
  encounterId: number | null
  isBusy: boolean
  isNotifyLocked: boolean
  isCheckedOut: boolean
  onOpenEncounter: () => void
  onOpenNotify: () => void
  onCallStarted: (statusTypeCode: number) => void
}

// Delete, notify, telehealth and encounter mapping all live on an existing appointment.
export const EditActionsBand: FC<Props> = ({
  appointment,
  encounterId,
  isBusy,
  isNotifyLocked,
  isCheckedOut,
  onOpenEncounter,
  onOpenNotify,
  onCallStarted,
}) => {
  const { setNotification } = useNotification()
  const telehealthLink = appointment?.jitsi_telehealth?.patient_short_link ?? ''

  // Some patients have no email or SMS consent, so staff send the link themselves.
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(telehealthLink)
      setNotification({
        title: 'Link copied',
        description: 'Telehealth link copied. Paste it into your email or SMS.',
        type: ALERT_CONSTANTS.SUCCESS,
      })
    } catch {
      setNotification({
        title: 'Copy failed',
        description: 'Could not copy the link. Please copy it manually.',
        type: ALERT_CONSTANTS.ERROR,
      })
    }
  }

  return (
    <div className={styles.editActions}>
      <div className={styles.editActionsRow}>
        <span className={styles.headerNote}>
          Encounter:{' '}
          <button type='button' className={styles.linkButton} onClick={onOpenEncounter}>
            {encounterId ?? 'Not Found'}
          </button>
        </span>

        {!!appointment?.access_code && (
          <span className={styles.headerNote}>
            {INFO_CONSTANTS.PATIENT_CODE}: {appointment.access_code}
          </span>
        )}
      </div>

      <div className={styles.editActionsRow}>
        {!isCheckedOut && (
          <Button
            proportion={BUTTON_PROPORTION.SMALL}
            modifier={BUTTON_MODIFIER.SECONDARY}
            disabled={isBusy || isNotifyLocked}
            onClick={onOpenNotify}>
            {BUTTON_CONSTANTS.NOTIFY_PATIENT}
          </Button>
        )}

        {!!telehealthLink && (
          <button
            type='button'
            className={styles.linkButton}
            title='Copy telehealth link to send manually'
            onClick={copyLink}>
            Copy telehealth link
          </button>
        )}

        <JitsiTelehealthCallButton
          schedule={appointment}
          disabled={isBusy}
          onCallStarted={onCallStarted}
        />

        {appointment?.last_notification && (
          <span className={styles.headerNote}>{appointment.last_notification}</span>
        )}
      </div>
    </div>
  )
}
